import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials, statsData } from '@/mocks/content';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const formSubmitAddr = 'https://readdy.ai/api/form/d9qm6vknlsngrm4lm1t0';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt')?.toString().trim() || '';
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch(formSubmitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '关于我们' },
  ];

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于抖阴pro',
    description: '抖阴pro品牌介绍、用户口碑与联系方式',
    url: 'https://douyinpro.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '抖阴pro',
      url: 'https://douyinpro.com/',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <div className="pt-20">
        <Breadcrumb items={breadcrumbItems} />

        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-4">
                关于我们
              </span>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                让每个人都能发现世界的精彩
              </h1>
              <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                抖阴pro致力于打造一个开放、多元、有趣的短视频内容生态，让创作者充分展示才华，让用户享受最优质的观看体验。
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-16">
              {statsData.map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-2xl bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-3">
                    <i className={`${stat.icon} text-primary-600 text-lg`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">{stat.value}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="mb-16">
              <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-3">品牌故事</h2>
              <div className="text-foreground-700 leading-relaxed space-y-4">
                <p>
                  抖阴pro诞生于对短视频内容生态的深度思考。我们观察到，虽然短视频已经成为人们日常生活中不可或缺的一部分，但用户在内容发现、创作体验和社区互动方面仍有许多未被满足的需求。
                </p>
                <p>
                  我们汇聚了一批来自全球顶尖科技公司的工程师、产品设计师和内容运营专家，历时两年精心打磨，推出了抖阴pro这款产品。我们的使命是：用技术赋能创作，用内容连接世界。
                </p>
                <p>
                  从最初的几百名内测用户，到今天数亿用户的选择，抖阴pro始终坚持以用户体验为中心，不断优化算法推荐、提升视频画质、丰富社交功能，让每一位用户都能在这里找到属于自己的精彩。
                </p>
              </div>
            </div>

            <div id="testimonials" className="mb-16">
              <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-2">用户口碑</h2>
              <p className="text-foreground-600 mb-8 leading-relaxed">来自真实用户的使用评价</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {testimonials.map((t) => (
                  <div key={t.id} className="p-6 rounded-2xl bg-background-100 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">{t.content}</p>
                    <div className="flex items-center gap-3">
                      <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                      <div>
                        <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                        <div className="text-xs text-foreground-500">{t.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div id="contact">
              <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-2">联系我们</h2>
              <p className="text-foreground-600 mb-8 leading-relaxed">如有任何问题或建议，欢迎通过以下方式联系我们</p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
                <div className="p-6 rounded-2xl bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-mail-line text-primary-600 text-xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-1">邮箱</h3>
                  <p className="text-sm text-foreground-500">support@douyinpro.com</p>
                </div>
                <div className="p-6 rounded-2xl bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-phone-line text-accent-600 text-xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-1">客服热线</h3>
                  <p className="text-sm text-foreground-500">400-888-9999</p>
                </div>
                <div className="p-6 rounded-2xl bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 rounded-xl bg-secondary-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-time-line text-secondary-600 text-xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-1">工作时间</h3>
                  <p className="text-sm text-foreground-500">周一至周日 9:00 - 22:00</p>
                </div>
              </div>

              <div className="rounded-2xl bg-background-100 border border-background-200/70 p-6 md:p-8">
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-6">发送消息</h3>
                <form onSubmit={handleSubmit} data-readdy-form="true" id="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                        placeholder="请输入您的邮箱地址"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200 resize-none"
                      placeholder="请输入您的消息内容（最多500字）"
                    ></textarea>
                  </div>
                  <div className="contact-spam-field">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <button
                    type="submit"
                    className="w-full md:w-auto px-8 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>

                  {formStatus === 'success' && (
                    <p className="mt-4 text-sm text-accent-600">消息已发送成功！我们会尽快回复您。</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="mt-4 text-sm text-primary-600">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}